import { useState, type FormEvent } from 'react';
import type { EmployeeProfileResponse, TalentDesignationsResponse } from '../../api/types';
import { apiPost, describeError } from '../../api/client';
import { useApiResource } from '../../hooks/useApiResource';
import { FactSection } from './FactSection';
import { formatCalendarDate, TALENT_DESIGNATION_SUGGESTIONS } from './attributeDisplay';
import listStyles from './FactRecords.module.css';
import factStyles from './CaptureSection.module.css';
import formStyles from './AssessmentForm.module.css';

/**
 * The Talent Designation section (`docs/DOMAIN_MODEL.md` §2.17;
 * `docs/UX_ARCHITECTURE.md` §7: "presented as time-boxed cards with
 * rationale and a review date, grouped by designation type, never as a
 * permanent badge on the profile header").
 *
 * ---------------------------------------------------------------------
 * WRITE PATH ADDED (Mission 166, implementing the specification
 * `docs/DOMAIN_DECISIONS.md`'s "Composite Concepts" (Mission 165)
 * authorised) — this section was read-only until this mission
 * (`docs/DOMAIN_DECISIONS.md` "Talent Designation Single-Active
 * Consistency Model": "no write path; preventive schema hardening ahead
 * of one"). Restructured as its own self-contained section (own
 * `useApiResource`, own `FactSection`), mirroring
 * `PotentialReadinessSection.tsx`'s own shape exactly — it now needs its
 * own reload trigger after a write, the same reason that section owns its
 * resource calls rather than being wired through the page.
 *
 * `canWrite` reuses `profile.viewerCanRecordPotentialReadiness` directly
 * — Mission 165 Part 4E decided Talent Designation's authority is
 * identical to Potential/Readiness's (`authorizePotentialReadinessWrite`),
 * so a second, redundant flag was not introduced for the same answer.
 *
 * UNLIKE POTENTIAL/READINESS, "ASSIGN" STAYS OFFERED EVEN WHEN NON-EMPTY
 *
 * Potential and Readiness are each a single current judgement — once one
 * exists, only "Correct"/"Withdraw" apply. Talent Designation is
 * different: "multiple TalentDesignations may coexist for one Employee"
 * (`docs/DOMAIN_MODEL.md` §2.17 — e.g. "High Potential" and "Succession
 * Candidate" at once), so "Assign talent designation" is always available
 * to `canWrite`, not only when the list is empty.
 *
 * ONE GROUP PER LABEL, NEVER A RANKING
 *
 * `label` is free text at the domain level — there is no fixed enum of
 * designations, and this section does not invent one, a score, or a
 * ranking. Grouped by label exactly as the API already groups them
 * (`CurrentTalentDesignationGroup`), never resorted or weighted against
 * each other. The Constitution's ten named examples are offered as a
 * `<datalist>` suggestion in the assignment form, never a closed choice.
 *
 * `status` is never rendered on a current record: every record in
 * `activeRecords` is, by construction, the current one for its label (the
 * domain's own `filterSuperseded`), so it is always `'active'` and would
 * tell a reader nothing beyond the record's presence here.
 *
 * `ambiguous` is the one thing this section must not hide: two
 * independent active records for the same (employee, label) is a
 * data-integrity signal the database constraint (migration 0030) now
 * prevents going forward, but pre-existing rows are never rewritten
 * (`talent_designation_forbid_mutation`) — a reader is told plainly rather
 * than the section silently picking one. Correct/Expire/Withdraw are
 * deliberately NOT offered on an ambiguous group: picking one of several
 * equally-current records to act on would be exactly the silent choice
 * this section otherwise refuses to make.
 * ---------------------------------------------------------------------
 */
export function TalentDesignationSection({ profile }: { profile: EmployeeProfileResponse }) {
  const canWrite = profile.viewerCanRecordPotentialReadiness;
  const designations = useApiResource<TalentDesignationsResponse>(`/employees/${profile.id}/talent-designations`);
  const [assigning, setAssigning] = useState(false);

  return (
    <>
      <FactSection
        id="talent-designation"
        title="Talent Designation"
        state={designations}
        loadingLabel="Loading talent designation…"
        errorTitle="Talent designation could not be loaded"
        isEmpty={(data) => data.designations.length === 0}
        empty={{
          title: 'No talent designation recorded',
          detail: canWrite
            ? 'Nothing has been recorded yet. Use "Assign talent designation" below to add one.'
            : 'Nothing has been recorded here. A talent designation is recorded when someone chooses to record one, so an empty list says nothing about this person’s potential or performance.',
        }}
        note="A time-boxed classification someone recorded about this person, with why and until when — never a score, a ranking, or something the system infers from ratings, skills or achievements."
      >
        {(data) => (
          <ul className={listStyles.list}>
            {data.designations.map((group) => (
              <li key={group.label} className={listStyles.record}>
                <div className={listStyles.primary}>
                  <span className={listStyles.name}>{group.label}</span>
                  {group.ambiguous ? (
                    <span
                      className={`${listStyles.marker} ${listStyles.markerAttention}`}
                      title="More than one independent active record exists for this designation. Both are shown below rather than one being silently chosen."
                    >
                      More than one active record
                    </span>
                  ) : null}
                </div>

                {group.activeRecords.map((record) => (
                  <TalentDesignationRecordCard
                    key={record.id}
                    employeeId={data.employeeId}
                    label={group.label}
                    record={record}
                    canWrite={canWrite && !group.ambiguous}
                    onChanged={() => designations.reload()}
                  />
                ))}
              </li>
            ))}
          </ul>
        )}
      </FactSection>

      {canWrite && designations.status === 'loaded' ? (
        assigning ? (
          <TalentDesignationForm
            employeeId={profile.id}
            onRecorded={() => {
              setAssigning(false);
              designations.reload();
            }}
            onClose={() => setAssigning(false)}
          />
        ) : (
          <button type="button" className={factStyles.addToggle} onClick={() => setAssigning(true)}>
            Assign talent designation
          </button>
        )
      ) : null}
    </>
  );
}

function TalentDesignationRecordCard({
  employeeId,
  label,
  record,
  canWrite,
  onChanged,
}: {
  employeeId: string;
  label: string;
  record: TalentDesignationsResponse['designations'][number]['activeRecords'][number];
  canWrite: boolean;
  onChanged: () => void;
}) {
  const [correcting, setCorrecting] = useState(false);
  const [ending, setEnding] = useState<'expiry' | 'withdrawal' | null>(null);

  return (
    <div>
      <dl className={listStyles.facts}>
        <div>
          <dt>Assigned by</dt>
          <dd>{record.assignedByName}</dd>
        </div>
        <div>
          <dt>Effective from</dt>
          <dd>{formatCalendarDate(record.effectiveFrom)}</dd>
        </div>
        <div>
          <dt>Review date</dt>
          <dd>
            {record.reviewDate === null ? (
              <span className={listStyles.absent}>Not recorded</span>
            ) : (
              formatCalendarDate(record.reviewDate)
            )}
          </dd>
        </div>
      </dl>

      {record.rationale ? <p className={listStyles.notes}>{record.rationale}</p> : null}

      {canWrite ? (
        <div className={factStyles.cardHeader}>
          <button
            type="button"
            className={factStyles.editToggle}
            onClick={() => {
              setCorrecting((open) => !open);
              setEnding(null);
            }}
          >
            {correcting ? 'Cancel' : 'Correct'}
          </button>
          <button
            type="button"
            className={factStyles.editToggle}
            onClick={() => {
              setEnding((open) => (open === 'expiry' ? null : 'expiry'));
              setCorrecting(false);
            }}
          >
            {ending === 'expiry' ? 'Cancel' : 'Expire'}
          </button>
          <button
            type="button"
            className={factStyles.editToggle}
            onClick={() => {
              setEnding((open) => (open === 'withdrawal' ? null : 'withdrawal'));
              setCorrecting(false);
            }}
          >
            {ending === 'withdrawal' ? 'Cancel' : 'Withdraw'}
          </button>
        </div>
      ) : null}

      {correcting ? (
        <TalentDesignationForm
          employeeId={employeeId}
          correcting={{ designationId: record.id, label, currentRationale: record.rationale, currentReviewDate: record.reviewDate }}
          onRecorded={() => {
            setCorrecting(false);
            onChanged();
          }}
          onClose={() => setCorrecting(false)}
        />
      ) : null}

      {ending ? (
        <TalentDesignationEndingForm
          employeeId={employeeId}
          designationId={record.id}
          label={label}
          action={ending}
          onEnded={() => {
            setEnding(null);
            onChanged();
          }}
          onClose={() => setEnding(null)}
        />
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------------
 * FORMS — shaped after PotentialReadinessSection.tsx's own pattern: local
 * field state, a small submit-state union, a re-read on success. Neither
 * form asks for an assessor or a "source type" — Talent Designation is a
 * Classification, not an Assessment (Mission 165 Part 1's boundary
 * matrix), and carries neither.
 * ------------------------------------------------------------------- */

type SubmitState = { status: 'editing' } | { status: 'submitting' } | { status: 'failed'; message: string };

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function TalentDesignationForm({
  employeeId,
  correcting,
  onRecorded,
  onClose,
}: {
  employeeId: string;
  correcting?: { designationId: string; label: string; currentRationale: string | null; currentReviewDate: string | null };
  onRecorded: () => void;
  onClose: () => void;
}) {
  const [label, setLabel] = useState(correcting?.label ?? '');
  const [rationale, setRationale] = useState(correcting?.currentRationale ?? '');
  const [effectiveFrom, setEffectiveFrom] = useState(todayIsoDate());
  const [reviewDate, setReviewDate] = useState(correcting?.currentReviewDate ?? '');
  const [state, setState] = useState<SubmitState>({ status: 'editing' });
  const submitting = state.status === 'submitting';
  const labelIsValid = label.trim().length > 0;
  const fieldId = (name: string) => `talent-designation-${name}${correcting ? `-${correcting.designationId}` : '-new'}`;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting || !labelIsValid) return;
    setState({ status: 'submitting' });
    try {
      const path = correcting
        ? `/employees/${employeeId}/talent-designations/${correcting.designationId}/correction`
        : `/employees/${employeeId}/talent-designations`;
      await apiPost(path, {
        ...(correcting ? {} : { label: label.trim() }),
        rationale: rationale.trim() || undefined,
        effectiveFrom,
        reviewDate: reviewDate || undefined,
      });
      onRecorded();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  return (
    <form
      className={formStyles.form}
      onSubmit={handleSubmit}
      aria-label={correcting ? 'Correct talent designation' : 'Assign talent designation'}
    >
      {correcting ? (
        <p className={formStyles.perspective}>
          This records a <strong>replacement</strong> for the current "{correcting.label}" designation. The earlier
          record is kept and stays on record; it stops counting as current.
        </p>
      ) : (
        <div className={formStyles.field}>
          <label htmlFor={fieldId('label')} className={formStyles.label}>
            Designation
          </label>
          <input
            id={fieldId('label')}
            className={formStyles.text}
            list={fieldId('suggestions')}
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            disabled={submitting}
            placeholder="e.g. High Potential"
            maxLength={200}
          />
          <datalist id={fieldId('suggestions')}>
            {TALENT_DESIGNATION_SUGGESTIONS.map((suggestion) => (
              <option key={suggestion} value={suggestion} />
            ))}
          </datalist>
        </div>
      )}

      <div className={formStyles.field}>
        <label htmlFor={fieldId('effective-from')} className={formStyles.label}>
          Effective from
        </label>
        <input
          id={fieldId('effective-from')}
          className={formStyles.text}
          type="date"
          value={effectiveFrom}
          onChange={(event) => setEffectiveFrom(event.target.value)}
          disabled={submitting}
          required
        />
      </div>

      <div className={formStyles.field}>
        <label htmlFor={fieldId('review-date')} className={formStyles.label}>
          Review date <span className={formStyles.optional}>optional</span>
        </label>
        <input
          id={fieldId('review-date')}
          className={formStyles.text}
          type="date"
          value={reviewDate}
          onChange={(event) => setReviewDate(event.target.value)}
          disabled={submitting}
        />
      </div>

      <div className={formStyles.field}>
        <label htmlFor={fieldId('rationale')} className={formStyles.label}>
          Rationale <span className={formStyles.optional}>optional</span>
        </label>
        <textarea
          id={fieldId('rationale')}
          className={formStyles.textarea}
          rows={2}
          value={rationale}
          onChange={(event) => setRationale(event.target.value)}
          disabled={submitting}
          placeholder="Why this designation applies"
          maxLength={2000}
        />
      </div>

      {state.status === 'failed' ? (
        <p className={formStyles.error} role="alert">
          {state.message}
        </p>
      ) : null}

      <div className={formStyles.actions}>
        <button type="submit" className={formStyles.primary} disabled={submitting || !labelIsValid}>
          {submitting ? 'Saving…' : correcting ? 'Record correction' : 'Assign designation'}
        </button>
        <button type="button" className={formStyles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** Expiry ("the review lapsed and it no longer applies") and withdrawal
 * ("assigned in error, or otherwise retracted") are the same shape — a
 * required reason, a terminal transition — so this is one component
 * parameterized by `action` rather than two near-identical copies. */
function TalentDesignationEndingForm({
  employeeId,
  designationId,
  label,
  action,
  onEnded,
  onClose,
}: {
  employeeId: string;
  designationId: string;
  label: string;
  action: 'expiry' | 'withdrawal';
  onEnded: () => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [state, setState] = useState<SubmitState>({ status: 'editing' });
  const submitting = state.status === 'submitting';
  const reasonIsValid = reason.trim().length > 0;
  const verb = action === 'expiry' ? 'Expire' : 'Withdraw';

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting || !reasonIsValid) return;
    setState({ status: 'submitting' });
    try {
      await apiPost(`/employees/${employeeId}/talent-designations/${designationId}/${action}`, { reason: reason.trim() });
      onEnded();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  return (
    <form className={formStyles.form} onSubmit={handleSubmit} aria-label={`${verb} talent designation`}>
      <p className={formStyles.perspective}>
        {verb} the <strong>{label}</strong> designation? It is not deleted — it stays on record, marked as{' '}
        {action === 'expiry' ? 'expired' : 'withdrawn'}, with the reason you give. This cannot be undone.
      </p>

      <div className={formStyles.field}>
        <label htmlFor={`talent-designation-${action}-reason-${designationId}`} className={formStyles.label}>
          Reason
        </label>
        <textarea
          id={`talent-designation-${action}-reason-${designationId}`}
          className={formStyles.textarea}
          rows={2}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          disabled={submitting}
          placeholder={action === 'expiry' ? 'Why this designation no longer applies' : 'Why this designation should not stand'}
          maxLength={2000}
        />
      </div>

      {state.status === 'failed' ? (
        <p className={formStyles.error} role="alert">
          {state.message}
        </p>
      ) : null}

      <div className={formStyles.actions}>
        <button type="submit" className={formStyles.primary} disabled={submitting || !reasonIsValid}>
          {submitting ? `${verb}ing…` : verb}
        </button>
        <button type="button" className={formStyles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}
